import { Bell, BellRing } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { ImplicitHandoffConsent, NotificationSettings, PushCategorySettings } from '@/types/api';
import { AuditedConsentCard } from '@/components/settings/AuditedConsentCard';
import { ToggleRow } from './ToggleRow';

interface NotificationsSectionProps {
  companionName: string;
  notifications: NotificationSettings;
  saving: boolean;
  onUpdateConsent: (patch: Record<string, unknown>) => void;
  isNative: boolean;
  pushCategories: PushCategorySettings;
  onUpdatePushCategory: (key: keyof PushCategorySettings, value: boolean) => void;
  // Auto-alert (isParent-gated)
  isParent: boolean;
  implicitHandoff: ImplicitHandoffConsent;
  /**
   * `HANDOFF_FAMILY_ENTRY_ENABLED`, as `GET /api/handoffs/family-pending`
   * reports it (`useHandoffFamilyEntry`). The two remaining auto-alert signals
   * (lifecycle, claim stress) create nothing while it is dark, so the card
   * that offers them is hidden with it (#3504, D-7).
   */
  handoffFamilyEntryEnabled: boolean;
}

export function NotificationsSection({
  companionName,
  notifications,
  saving,
  onUpdateConsent,
  isNative,
  pushCategories,
  onUpdatePushCategory,
  isParent,
  implicitHandoff,
  handoffFamilyEntryEnabled,
}: NotificationsSectionProps) {
  const { t } = useTranslation('settings');
  const companion = companionName;
  const signalOff = t('notifications.autoAlert.off');
  return (
    <>
      {/* ── Communication Preferences ───────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle>{t('notifications.communication.title')}</CardTitle>
          </div>
          <CardDescription>{t('notifications.communication.description', { companion })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label={t('notifications.communication.email')}
            description={t('notifications.communication.emailDescription')}
            checked={notifications.email}
            disabled={saving}
            onCheckedChange={(v) => onUpdateConsent({ notifications_email: v })}
          />
          {/* SMS is single-sourced through the audited consent card below (TCPA
              opt-in requires the FCC disclosure) — no unaudited toggle here. */}
          <ToggleRow
            label={t('notifications.communication.push')}
            description={isNative
              ? t('notifications.communication.pushNative')
              : t('notifications.communication.pushBrowser')}
            checked={notifications.push}
            disabled={saving}
            onCheckedChange={(v) => onUpdateConsent({ notifications_push: v })}
          />
          {isNative && notifications.push && (
            <div className="mt-4 pt-4 border-t border-border space-y-3">
              <div>
                <h4 className="text-body font-medium">{t('notifications.pushCategories.title')}</h4>
                <p className="text-caption text-stevens-ink-3 mt-1">
                  {t('notifications.pushCategories.description')}
                </p>
              </div>
              <ToggleRow
                label={t('notifications.pushCategories.handoffNudge')}
                description={t('notifications.pushCategories.handoffNudgeDescription', { companion })}
                checked={pushCategories.handoff_nudge}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('handoff_nudge', v)}
              />
              <ToggleRow
                label={t('notifications.pushCategories.trferClaimed')}
                description={t('notifications.pushCategories.trferClaimedDescription')}
                checked={pushCategories.trfer_claimed}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('trfer_claimed', v)}
              />
              <ToggleRow
                label={t('notifications.pushCategories.trferMessage')}
                description={t('notifications.pushCategories.trferMessageDescription')}
                checked={pushCategories.trfer_message}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('trfer_message', v)}
              />
              <ToggleRow
                label={t('notifications.pushCategories.handback')}
                description={t('notifications.pushCategories.handbackDescription', { companion })}
                checked={pushCategories.handback}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('handback', v)}
              />
              <ToggleRow
                label={t('notifications.pushCategories.taskDue')}
                description={t('notifications.pushCategories.taskDueDescription')}
                checked={pushCategories.task_due}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('task_due', v)}
              />
              <ToggleRow
                label={t('notifications.pushCategories.grantDecision')}
                description={t('notifications.pushCategories.grantDecisionDescription')}
                checked={pushCategories.grant_decision}
                disabled={saving}
                onCheckedChange={(v) => onUpdatePushCategory('grant_decision', v)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audited legal consent (SMS opt-in w/ TCPA disclosure, marketing email,
          polls) — single source for these, folded in from the former
          /settings/notifications page so all notification settings live here. */}
      <AuditedConsentCard />

      {/* ── Auto-alert TRF on signals ─────────────────────── */}
      {/* #3504, D-7: two switches left this card. "If a message sounds like a
          crisis" promised TRF would hear; TRF is not a crisis line
          (CRISIS_HANDOFF_RETIRED, signalEvaluators/critical-chat.js), so
          nothing does. "If things go quiet after a hard day" gated an
          evaluator that matches a message `sentiment` tag nothing writes
          (signalEvaluators/silence.js), so it could never fire. The two left
          create a nudge only while the family-entry handoff flag is lit, so
          the card shows only then. */}
      {isParent && handoffFamilyEntryEnabled && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-primary" />
              <CardTitle>{t('notifications.autoAlert.title')}</CardTitle>
            </div>
            <CardDescription>
              {t('notifications.autoAlert.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 pr-4">
                <Label htmlFor="implicit-lifecycle" className="text-body font-medium">
                  {t('notifications.autoAlert.lifecycle')}
                </Label>
                <p className="text-caption text-stevens-ink-3">
                  {implicitHandoff.lifecycle ? t('notifications.autoAlert.lifecycleOn') : signalOff}
                </p>
              </div>
              <Switch
                id="implicit-lifecycle"
                checked={implicitHandoff.lifecycle}
                disabled={saving}
                onCheckedChange={(v) => onUpdateConsent({ implicit_handoff: { lifecycle: v } })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5 pr-4">
                <Label htmlFor="implicit-claim-stress" className="text-body font-medium">
                  {t('notifications.autoAlert.claimStress')}
                </Label>
                <p className="text-caption text-stevens-ink-3">
                  {implicitHandoff.claim_stress ? t('notifications.autoAlert.claimStressOn') : signalOff}
                </p>
              </div>
              <Switch
                id="implicit-claim-stress"
                checked={implicitHandoff.claim_stress}
                disabled={saving}
                onCheckedChange={(v) => onUpdateConsent({ implicit_handoff: { claim_stress: v } })}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
